/**
 * Container for the extraction data section.
 *
 * Parses the raw `extractedData` JSON into typed categories, manages
 * the inline add-line-item form state, the inline edit-line-item state
 * machine, the delete-confirmation dialog state, and the per-category
 * "Mark Conflicts Resolved" action. Per-category conflict counts are
 * derived synchronously from the parsed items so category-header badges
 * render on first paint without requiring user interaction.
 *
 * Pre-binds per-category expand handlers AND per-category
 * resolve-conflicts handlers so the View never creates functions
 * inside JSX. Per-item edit/delete handlers are passed by reference
 * with the `(category, index)` tuple as arguments since binding one
 * handler per `(category, index)` would explode the prop surface.
 * @remarks Edit + delete mutations refetch the extraction query on
 * success so the rendered items reflect the new server state. While
 * the refetch is in flight (and for one render cycle after) the
 * affected `(category, index)` is held in `recentlyModified` so the
 * View can surface the "blue highlight" required by SE-4384's AC. The
 * highlight is cleared the next time the upstream `extractedData`
 * reference changes (i.e. after the refetch completes), avoiding a
 * stale highlight on items that no longer exist.
 *
 * Failed `editLineItem` / `deleteLineItem` / `resolveCategoryConflicts`
 * mutations surface targeted error messages so the user can retry
 * explicitly rather than experiencing a silent no-op. Each error is
 * cleared on the next successful action against the same target.
 * @module features/contracts/components/ExtractionDataSection/ExtractionDataSectionContainer
 */

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useIsContractOwner } from "@/features/contracts/hooks/useIsContractOwner";
import { useContractLineItemMutations } from "@/features/contracts/hooks/useContractLineItemMutations";
import type { ContractLineItemMutations } from "@/features/contracts/hooks/useContractLineItemMutations";
import { useInlineEditDirtyContext } from "@/features/contracts/hooks/useInlineEditDirtyTracker";
import type { ExtractionLineItem } from "@/features/contracts/types";

import ExtractionDataSectionView from "./ExtractionDataSectionView";

/** Props for {@link ExtractionDataSectionContainer}. */
interface Props {
  /** UUID of the extraction whose data is being rendered. */
  readonly extractionId: string;
  /** Raw extracted data object with category keys mapping to item arrays. */
  readonly extractedData: Record<string, unknown>;
  /** UUID of the player whose extraction data is displayed. */
  readonly playerId: string;
  /** Document type of the extraction data. */
  readonly documentType: string;
  /**
   * When `true`, the section renders in read-only mode: no "Add line
   * item" button, no per-category "Mark Conflicts Resolved" button,
   * and the inline add form is suppressed. Used after the extraction
   * has been saved (status transitions to `SAVED`) to present the
   * data as immutable per Story 2.5 acceptance criteria.
   * Defaults to `false` (full editing affordances visible).
   */
  readonly readOnly?: boolean;
  /**
   * When provided, only the category matching this key is rendered
   * and the inline category heading is suppressed (the parent wrapper
   * is expected to render its own heading, e.g. the
   * `CollapsibleTierHeader` from `ContractExtractionView`). Defaults
   * to undefined so legacy callers render every category with the
   * original heading intact.
   */
  readonly categoryFilter?: string;
  /**
   * Optional pre-mounted mutation bundle (perf #11 F1 — SE-4333 item N).
   * When provided, the section uses these shared mutation callbacks
   * instead of mounting its own four `useMutation` hooks. Passing
   * `mutations` avoids the 4-hook-per-section allocation explosion
   * that used to fire on every drawer open (24 mutation hooks on a
   * six-category contract). Kept optional so tests and external
   * embedders that don't wire a shared parent continue to work — the
   * container falls back to mounting its own hooks when the prop is
   * absent.
   */
  readonly mutations?: ContractLineItemMutations;
}

/** A parsed category with its items and derived conflict count. */
export interface ParsedCategory {
  readonly name: string;
  readonly items: readonly ExtractionLineItem[];
  readonly unresolvedConflictCount: number;
}

/**
 * Identifies the line item currently being edited inline, with the
 * working draft text held in component state until the user saves or
 * cancels.
 */
export interface EditingItemState {
  readonly category: string;
  readonly index: number;
  readonly draftText: string;
}

/**
 * Identifies the line item awaiting delete confirmation. The
 * confirmation dialog is open whenever this is non-null.
 */
export interface DeletingItemState {
  readonly category: string;
  readonly index: number;
}

/** Per-target error message after a failed mutation. */
export interface ItemErrorState {
  readonly category: string;
  readonly index: number;
  readonly message: string;
}

/**
 * Type guard for a raw extractedData item matching the canonical
 * `ExtractedTerm` shape `{ text, isConflict, confidenceNote }` produced
 * by the backend Bedrock prompt.
 * @param item - Candidate value from a category array
 * @returns Whether the item has the expected shape
 */
function isExtractionLineItem(item: unknown): item is ExtractionLineItem {
  if (typeof item !== "object" || item === null) return false;
  const candidate = item as Record<string, unknown>;
  return (
    typeof candidate.text === "string" &&
    typeof candidate.isConflict === "boolean" &&
    typeof candidate.confidenceNote === "string"
  );
}

/**
 * Parses a raw category value into typed line items, dropping any
 * entries that do not match the canonical shape.
 * @param raw - The raw value from extractedData for a category
 * @returns Array of typed line items
 */
function parseCategoryItems(raw: unknown): readonly ExtractionLineItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isExtractionLineItem).map(item => ({
    text: item.text,
    isConflict: item.isConflict,
    confidenceNote: item.confidenceNote,
  }));
}

/**
 * Stable string key for a `(category, index)` tuple used by the
 * `recentlyModified` set.
 * @param category - Category name
 * @param index - Zero-based item index within the category
 * @returns Composite key
 */
export function recentlyModifiedKey(category: string, index: number): string {
  return `${category}:${index}`;
}

/**
 * State updater that clears a per-item error when its target matches
 * `(category, index)`, preserving the existing reference otherwise.
 * Extracted as a pure helper so the branching is independently
 * testable without going through the async mutation success path
 * (which trips React 19's cleanup null-throw in jest).
 * @param category - Category of the item just touched
 * @param index - Index of the item just touched
 * @returns Updater suitable for `useState`'s setter
 */
export function clearItemErrorIfTargetMatches(
  category: string,
  index: number
): (prev: ItemErrorState | null) => ItemErrorState | null {
  return prev =>
    prev !== null && prev.category === category && prev.index === index
      ? null
      : prev;
}

/**
 * State updater that clears a per-item error whenever its category
 * matches, regardless of index. Used after a delete so stale
 * `(category, index)` error pointers — which shift after deletion —
 * do not silently persist on the wrong row.
 * @param category - Category of the item just deleted
 * @returns Updater suitable for `useState`'s setter
 */
export function clearItemErrorIfCategoryMatches(
  category: string
): (prev: ItemErrorState | null) => ItemErrorState | null {
  return prev => (prev !== null && prev.category === category ? null : prev);
}

/**
 * State updater that clears the inline editing state whenever its
 * category matches, regardless of index. Used after a delete so a
 * stale `editingItem` pointer does not drift to the wrong row when
 * later items in the same category shift their indexes.
 * @param category - Category of the item just deleted
 * @returns Updater suitable for `useState`'s setter
 */
export function clearEditingItemIfCategoryMatches(
  category: string
): (prev: EditingItemState | null) => EditingItemState | null {
  return prev => (prev !== null && prev.category === category ? null : prev);
}

/**
 * State updater that drops a single category's entry from the
 * resolveErrors map, preserving the existing reference when the entry
 * doesn't exist.
 * @param category - Category to clear
 * @returns Updater suitable for `useState`'s setter
 */
export function clearResolveErrorForCategory(
  category: string
): (
  prev: Readonly<Record<string, string>>
) => Readonly<Record<string, string>> {
  return prev => {
    if (prev[category] === undefined) return prev;
    const { [category]: _removed, ...rest } = prev;
    return rest;
  };
}

/**
 * State updater that adds a key to the recentlyModified set,
 * returning a new Set instance so React detects the state change.
 * @param key - Composite `${category}:${index}` key
 * @returns Updater suitable for `useState`'s setter
 */
export function addRecentlyModifiedKey(
  key: string
): (prev: ReadonlySet<string>) => ReadonlySet<string> {
  return prev => new Set([...prev, key]);
}

/**
 * State updater that drops every recentlyModified key belonging to a
 * given category. After a delete lands, every subsequent item in that
 * category shifts left by one, so any remembered `${category}:index`
 * keys now point at the wrong row — the safe move is to drop them all
 * and let the user re-trigger the highlight by editing again. Preserves
 * the original reference when nothing matches.
 * @param category - Category whose keys should be dropped
 * @returns Updater suitable for `useState`'s setter
 */
export function dropRecentlyModifiedForCategory(
  category: string
): (prev: ReadonlySet<string>) => ReadonlySet<string> {
  const prefix = `${category}:`;
  return prev => {
    const filtered = [...prev].filter(key => !key.startsWith(prefix));
    if (filtered.length === prev.size) return prev;
    return new Set(filtered);
  };
}

/**
 * State updater that reflects a resolveCategoryConflicts mutation
 * outcome on the per-category resolve errors map: success clears any
 * existing entry; failure writes the user-facing retry message.
 * @param category - Category that was resolved
 * @param ok - Whether the mutation reported success
 * @returns Updater suitable for `useState`'s setter
 */
export function applyResolveOutcomeToErrors(
  category: string,
  ok: boolean
): (
  prev: Readonly<Record<string, string>>
) => Readonly<Record<string, string>> {
  return prev => {
    if (ok) {
      if (prev[category] === undefined) return prev;
      const { [category]: _removed, ...rest } = prev;
      return rest;
    }
    return {
      ...prev,
      [category]: "Couldn't mark conflicts resolved. Please try again.",
    };
  };
}

/**
 * Container for the extraction data section.
 * @param props - Component props
 * @param props.extractionId - Extraction UUID (drives all mutations)
 * @param props.extractedData - Raw extracted data keyed by category
 * @param props.playerId - Player UUID
 * @param props.documentType - Document type
 * @param props.readOnly - When `true`, suppresses all editing affordances
 * @param props.categoryFilter - When provided, only that category renders
 *   and its inline heading is suppressed (parent owns the header)
 * @param props.mutations - Pre-mounted mutation bundle (perf #11 F1).
 *   Optional — when omitted, the container mounts its own.
 * @returns The extraction data section view bound to live state
 */
function ExtractionDataSectionContainer({
  extractionId,
  extractedData,
  playerId,
  documentType,
  readOnly = false,
  categoryFilter,
  mutations,
}: Props) {
  // SE-4350 defense-in-depth: non-OWNER callers are coerced into
  // read-only so no add / edit / delete / resolve affordance can
  // render even if a direct embedder forgets to pass `readOnly`.
  // The hooks that follow still run unconditionally to respect
  // rules-of-hooks; the effective flag is passed down to the View
  // and also gates the mutation handlers below.
  const isOwner = useIsContractOwner();
  const effectiveReadOnly = readOnly || !isOwner;

  // Perf #11 F1 (SE-4333 item N): prefer mutations injected from a
  // shared parent (`ContractExtractionViewContainer`) so the drawer
  // only mounts four useMutation hooks in total instead of 4×N where
  // N is the number of expanded categories. Fall back to mounting
  // our own bundle so this container stays usable standalone (tests
  // and any external embedder that doesn't wire a shared parent).
  // Calling `useContractLineItemMutations` unconditionally preserves
  // hook-order; the returned bundle is discarded when `mutations`
  // was supplied.
  const localMutations = useContractLineItemMutations();
  const effectiveMutations = mutations ?? localMutations;

  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [newItemText, setNewItemText] = useState("");
  const [resolvingCategory, setResolvingCategory] = useState<string | null>(
    null
  );
  const [resolveErrors, setResolveErrors] = useState<
    Readonly<Record<string, string>>
  >({});

  const [editingItem, setEditingItem] = useState<EditingItemState | null>(null);
  const [editError, setEditError] = useState<ItemErrorState | null>(null);

  const [deletingItem, setDeletingItem] = useState<DeletingItemState | null>(
    null
  );
  const [deleteError, setDeleteError] = useState<ItemErrorState | null>(null);

  const [recentlyModified, setRecentlyModified] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  /**
   * Synchronous ref guard that prevents concurrent resolve invocations even
   * before React commits the `resolvingCategory` state update. State reads
   * are stale until the next render; the ref is mutated synchronously, so a
   * second rapid click on the same (or any other) category is blocked
   * immediately — avoiding duplicate audit-log entries on the backend.
   */
  const resolvingCategoryRef = useRef<string | null>(null);

  const {
    addLineItem,
    editLineItem,
    deleteLineItem,
    resolveCategoryConflicts,
    isAdding,
    isEditingMutation,
    isDeletingMutation,
    isResolving,
  } = effectiveMutations;

  /**
   * Tracks the `extractedData` reference seen on the previous render so
   * the `recentlyModified` clearing effect can detect when an upstream
   * refetch has completed. The blue-highlight contract is "until saved"
   * — once the refetch lands and re-renders the items, the highlight
   * has served its purpose and would otherwise risk pointing at a stale
   * `(category, index)` tuple if a delete shifted indexes.
   */
  const previousExtractedDataRef = useRef(extractedData);
  /**
   * Synchronous in-flight guard for delete mutations. React state updates
   * are async, so `isDeletingMutation` may not be `true` by the time a
   * second tap reaches the handler. This ref blocks the second invocation
   * before any re-render occurs.
   */
  const deletingInFlightRef = useRef(false);

  const categories = useMemo<readonly ParsedCategory[]>(() => {
    return Object.keys(extractedData).map(key => {
      const items = parseCategoryItems(extractedData[key]);
      const unresolvedConflictCount = items.reduce(
        (acc, item) => acc + (item.isConflict ? 1 : 0),
        0
      );
      return { name: key, items, unresolvedConflictCount };
    });
  }, [extractedData]);

  // Which categories are visible is a filtering decision, so the container
  // makes it and the View renders the result (SE-8019).
  const visibleCategories =
    categoryFilter === undefined
      ? categories
      : categories.filter(c => c.name === categoryFilter);

  const expandHandlers = useMemo<Readonly<Record<string, () => void>>>(
    () =>
      Object.fromEntries(
        categories.map(cat => [
          cat.name,
          () => {
            setExpandedCategory(cat.name);
            setNewItemText("");
            setResolveErrors(clearResolveErrorForCategory(cat.name));
          },
        ])
      ),
    [categories]
  );

  const resolveConflictHandlers = useMemo<
    Readonly<Record<string, () => Promise<void>>>
  >(
    () =>
      Object.fromEntries(
        categories.map(cat => [
          cat.name,
          async () => {
            if (effectiveReadOnly) return;
            // eslint-disable-next-line react-hooks/refs -- ref read fires inside an async user-event handler, never during render. The lint rule cannot tell the difference.
            if (resolvingCategoryRef.current !== null) return;
            resolvingCategoryRef.current = cat.name;
            setResolvingCategory(cat.name);
            try {
              const ok = await resolveCategoryConflicts(
                extractionId,
                cat.name,
                playerId,
                documentType
              );
              setResolveErrors(applyResolveOutcomeToErrors(cat.name, ok));
            } finally {
              resolvingCategoryRef.current = null;
              setResolvingCategory(null);
            }
          },
        ])
      ),
    [
      categories,
      effectiveReadOnly,
      resolveCategoryConflicts,
      extractionId,
      playerId,
      documentType,
    ]
  );

  const handleCollapseCategory = useCallback(() => {
    setExpandedCategory(null);
    setNewItemText("");
  }, []);

  const handleNewItemTextChange = useCallback((text: string) => {
    setNewItemText(text);
  }, []);

  const handleSubmitLineItem = useCallback(async () => {
    // SE-4350 defense-in-depth: the view hides the add affordance when
    // `effectiveReadOnly` is true, but this early return ensures the
    // mutation cannot fire even if a consumer invokes the callback
    // imperatively (tests, portaled triggers, or a regression that
    // forgets to gate the button).
    if (effectiveReadOnly) return;
    if (!expandedCategory || newItemText.trim() === "") {
      return;
    }
    const targetCategory = expandedCategory;
    const ok = await addLineItem(
      extractionId,
      targetCategory,
      newItemText.trim(),
      playerId,
      documentType
    );
    if (ok) {
      // Mark the just-appended item (last index in the category after refetch)
      // as recently modified so it gets the blue highlight. We compute the
      // index from the *current* parsed categories — after the refetch
      // re-renders the container, the new item lives at this same index
      // because the backend appends.
      const cat = categories.find(c => c.name === targetCategory);
      const newIndex = cat ? cat.items.length : 0;
      const newKey = recentlyModifiedKey(targetCategory, newIndex);
      setRecentlyModified(addRecentlyModifiedKey(newKey));
      setExpandedCategory(null);
      setNewItemText("");
    }
  }, [
    effectiveReadOnly,
    expandedCategory,
    newItemText,
    addLineItem,
    extractionId,
    playerId,
    documentType,
    categories,
  ]);

  const handleStartEdit = useCallback(
    (category: string, index: number, currentText: string) => {
      if (effectiveReadOnly) return;
      setEditingItem({ category, index, draftText: currentText });
      setEditError(clearItemErrorIfTargetMatches(category, index));
    },
    [effectiveReadOnly]
  );

  const handleChangeEditDraft = useCallback((text: string) => {
    setEditingItem(prev =>
      prev === null ? prev : { ...prev, draftText: text }
    );
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingItem(null);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (effectiveReadOnly) return;
    if (editingItem === null) return;
    const { category, index, draftText } = editingItem;
    const trimmed = draftText.trim();
    if (trimmed === "") return;
    const ok = await editLineItem(
      extractionId,
      category,
      index,
      trimmed,
      playerId,
      documentType
    );
    if (ok) {
      const editedKey = recentlyModifiedKey(category, index);
      setRecentlyModified(addRecentlyModifiedKey(editedKey));
      setEditingItem(null);
      setEditError(clearItemErrorIfTargetMatches(category, index));
    } else {
      setEditError({
        category,
        index,
        message: "Couldn't save changes. Please try again.",
      });
    }
  }, [
    effectiveReadOnly,
    editingItem,
    editLineItem,
    extractionId,
    playerId,
    documentType,
  ]);

  const handleRequestDelete = useCallback(
    (category: string, index: number) => {
      if (effectiveReadOnly) return;
      setDeletingItem({ category, index });
      setDeleteError(prev =>
        prev !== null && prev.category === category && prev.index === index
          ? null
          : prev
      );
    },
    [effectiveReadOnly]
  );

  const handleCancelDelete = useCallback(() => {
    // Ignore cancel-while-deleting so the dialog can't be dismissed mid
    // mutation (backdrop / Escape routes here too).
    if (isDeletingMutation) return;
    setDeletingItem(null);
  }, [isDeletingMutation]);

  const handleConfirmDelete = useCallback(async () => {
    if (effectiveReadOnly) return;
    if (deletingItem === null || deletingInFlightRef.current) return;
    deletingInFlightRef.current = true;
    const { category, index } = deletingItem;
    try {
      const ok = await deleteLineItem(
        extractionId,
        category,
        index,
        playerId,
        documentType
      );
      if (ok) {
        setDeletingItem(null);
        setDeleteError(clearItemErrorIfTargetMatches(category, index));
        // Row state is keyed by `(category, index)`, but a delete shifts
        // every subsequent item in the category left by one. Drop any
        // editing pointer / per-row error / recentlyModified entry that
        // was targeting the affected category to avoid a stale pointer
        // overwriting the wrong row on the next save.
        setEditingItem(clearEditingItemIfCategoryMatches(category));
        setEditError(clearItemErrorIfCategoryMatches(category));
        setRecentlyModified(dropRecentlyModifiedForCategory(category));
      } else {
        setDeleteError({
          category,
          index,
          message: "Couldn't delete this item. Please try again.",
        });
      }
    } finally {
      deletingInFlightRef.current = false;
    }
  }, [
    effectiveReadOnly,
    deletingItem,
    deleteLineItem,
    extractionId,
    playerId,
    documentType,
  ]);

  /**
   * SE-4347 AC-3 Option A — publish UI-layer dirtiness for the NEW
   * line-item row (the expanded add form) and for the currently-open
   * inline edit draft so the drawer-level
   * {@link useContractExtractionEditMode}.isDirty folds in signals that
   * never reach `extractedData`. The row ids include `extractionId` so
   * independently-mounted sections in the same tree (e.g. primary
   * contract + transfer agreement panes) cannot collide on a shared
   * category key.
   *
   * Row id conventions:
   * - `add:<extractionId>:<category>` — dirty while the add form has
   *   non-whitespace text. Goes clean on submit / collapse / empty.
   * - `edit:<extractionId>:<category>:<index>` — dirty while the
   *   inline-edit draft diverges from the saved text. Goes clean on
   *   cancel, successful save, or if the user types the original text
   *   back verbatim.
   */
  const { markDirty, markClean } = useInlineEditDirtyContext();

  const addRowId = useMemo<string | null>(
    () =>
      expandedCategory !== null
        ? `add:${extractionId}:${expandedCategory}`
        : null,
    [extractionId, expandedCategory]
  );
  const isAddRowDirty = addRowId !== null && newItemText.trim().length > 0;

  const editOriginalText = useMemo<string | null>(() => {
    if (editingItem === null) return null;
    const category = categories.find(c => c.name === editingItem.category);
    return category?.items[editingItem.index]?.text ?? null;
  }, [categories, editingItem]);

  const editRowId = useMemo<string | null>(
    () =>
      editingItem !== null
        ? `edit:${extractionId}:${editingItem.category}:${editingItem.index}`
        : null,
    [extractionId, editingItem]
  );
  const isEditRowDirty =
    editingItem !== null &&
    editOriginalText !== null &&
    editingItem.draftText !== editOriginalText;

  /**
   * Clear `recentlyModified` whenever the upstream `extractedData`
   * reference changes (i.e. the refetch landed). See the
   * `previousExtractedDataRef` declaration above for the rationale.
   */
  useEffect(() => {
    if (previousExtractedDataRef.current !== extractedData) {
      previousExtractedDataRef.current = extractedData;
      setRecentlyModified(prev => (prev.size === 0 ? prev : new Set()));
    }
  }, [extractedData]);

  useEffect(() => {
    if (addRowId === null || !isAddRowDirty) return;
    markDirty(addRowId);
    return () => markClean(addRowId);
  }, [addRowId, isAddRowDirty, markDirty, markClean]);

  useEffect(() => {
    if (editRowId === null || !isEditRowDirty) return;
    markDirty(editRowId);
    return () => markClean(editRowId);
  }, [editRowId, isEditRowDirty, markDirty, markClean]);

  return (
    <ExtractionDataSectionView
      visibleCategories={visibleCategories}
      expandedCategory={expandedCategory}
      newItemText={newItemText}
      isAdding={isAdding}
      resolvingCategory={resolvingCategory}
      isResolving={isResolving}
      resolveErrors={resolveErrors}
      expandHandlers={expandHandlers}
      resolveConflictHandlers={resolveConflictHandlers}
      editingItem={editingItem}
      isEditingMutation={isEditingMutation}
      editError={editError}
      deletingItem={deletingItem}
      isDeletingMutation={isDeletingMutation}
      deleteError={deleteError}
      recentlyModified={recentlyModified}
      onCollapseCategory={handleCollapseCategory}
      onNewItemTextChange={handleNewItemTextChange}
      onSubmitLineItem={handleSubmitLineItem}
      onStartEdit={handleStartEdit}
      onChangeEditDraft={handleChangeEditDraft}
      onSaveEdit={handleSaveEdit}
      onCancelEdit={handleCancelEdit}
      onRequestDelete={handleRequestDelete}
      onConfirmDelete={handleConfirmDelete}
      onCancelDelete={handleCancelDelete}
      readOnly={effectiveReadOnly}
      categoryFilter={categoryFilter}
    />
  );
}

ExtractionDataSectionContainer.displayName = "ExtractionDataSectionContainer";

export default memo(ExtractionDataSectionContainer);
